import React, { useState, useEffect } from 'react';
import { Grid, Button, MenuItem, TextField } from '@mui/material';

export interface SearchFieldConfig {
  name: string;
  label: string;
  type?: string;
  icon?: React.ReactNode;
  options?: Array<{ value: string | number; label: string }>;
  sx?: object;
}

export interface SearchButtonConfig {
  name: string;
  label: string;
  variant?: 'contained' | 'outlined';
  color?: 'primary' | 'secondary';
  icon?: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  onClick: (values: Record<string, string>) => void;
}

interface SearchComponentProps {
  fields: SearchFieldConfig[];
  buttons: SearchButtonConfig[];
  resetKey?: number;
}

export const SearchComponent: React.FC<SearchComponentProps> = ({ fields, buttons, resetKey }) => {
  const [values, setValues] = useState<Record<string, string>>(
    () => fields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {} as Record<string, string>)
  );

  useEffect(() => {
    setValues(fields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {} as Record<string, string>));
  }, [resetKey]);

  const searchBtn = buttons.find(b => b.type === 'submit' || b.name === 'search');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchBtn) {
      searchBtn.onClick(values);
    }
  };

  return (
    <Grid
      container
      spacing={2}
      mt={1}
      mb={3}
      style={{ backgroundColor: '#f5f5f5', marginLeft: '0px', padding: '16px', borderRadius: '6px', width: '100%' }}
      alignItems="center"
    >
      {fields.map((field) => (
        <Grid
          item
          xs={12}
          sm={6}
          md={3}
          style={{ padding: '0px 8px' }}
          key={field.name}
          sx={{ mt: { xs: 2, sm: 1, md: 0 } }}
        >
          <TextField
            name={field.name}
            label={field.label}
            type={field.type || 'text'}
            select={Boolean(field.options)}
            value={values[field.name] ?? ''}
            onChange={(e) => setValues(prev => ({ ...prev, [field.name]: e.target.value }))}
            onKeyDown={handleKeyDown}
            size="small"
            sx={{
              width: '100%',
              '& .MuiFormLabel-root': { fontSize: '12px' },
              ...field.sx,
            }}
            InputProps={{
              endAdornment: field.icon,
            }}
          >
            {field.options?.map((option) => (
              <MenuItem key={option.label} value={option.label}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      ))}
      <Grid
        item
        xs={12}
        sm={12}
        md={3}
        style={{ padding: '0px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
        sx={{
          mt: { xs: 2, sm: 2, md: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 2 },
        }}
      >
        {buttons.map((btn) => (
          <Button
            key={btn.name}
            variant={btn.variant || 'contained'}
            color={btn.color || 'primary'}
            type={btn.type || 'button'}
            startIcon={btn.icon}
            onClick={() => btn.onClick(values)}
            sx={{
              mr: { xs: 0, sm: 0 },
              mb: { xs: 1, sm: 0 },
              width: { xs: '100%', sm: 'auto' },
            }}
            fullWidth={window.innerWidth < 900}
          >
            {btn.label}
          </Button>
        ))}
      </Grid>
    </Grid>
  );
};
